// Dedicated executive case-study content per project.
// Each entry powers /projects/<slug>.
//
// Video activation:
//   When you have a final hero video, set `hero.video` to the file URL
//   (mp4/webm). It will autoplay (muted, looped, inline) and replace the
//   static hero image. Leave empty to keep the static image.

export const caseStudies = {
  // ------------------------------------------------------------------
  // MADRID HORSE WEEK — FINAL CONTENT
  // ------------------------------------------------------------------
  "madrid-horse-week": {
    id: "madrid-horse-week",
    eyebrow: "Experience · FEI World Cup",
    title: "Madrid Horse Week",
    subtitle: "Executive Director & Board Member",
    headline: "Building one of Europe's leading FEI World Cup events.",
    years: "2012 — 2021",
    location: "IFEMA · Madrid · Spain",
    hero: {
      image:
        "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/50umk0f6_mhw4.jpg",
      video: "", // upload final B-roll mp4 here to activate auto-loop hero video
      poster: "",
    },
    summary: [
      `For more than eight consecutive editions, I played a central role in the development, management and growth of Madrid Horse Week, one of Europe's premier FEI World Cup events and Spain's most important indoor equestrian competition.`,
      `As Executive Director and Board Member, I was responsible for the strategic, commercial and operational leadership of the event, overseeing relationships with sponsors, institutions, media partners, venue operators and key stakeholders while helping transform Madrid Horse Week into a leading sports and entertainment platform.`,
    ],
    mission: `Our objective was not simply to organise a horse show. The vision was to create a world-class sporting, social and entertainment experience capable of attracting new audiences, increasing sponsor value and positioning equestrian sport at the centre of Madrid's cultural and sporting calendar.`,
    responsibilities: [
      "Executive leadership of the event",
      "Board member responsibilities and strategic planning",
      "Sponsorship sales and commercial partnerships",
      "VIP hospitality programmes",
      "Ticketing strategy and audience growth",
      "Television production and broadcast supervision",
      "Media rights and media relations",
      "Institutional and government relations",
      "Venue negotiations with IFEMA",
      "Coordination of multiple operational teams",
      "Supervision of public funding and tax incentive programmes",
      "Management of sponsor activation programmes",
      "Strategic positioning and long-term growth planning",
    ],
    achievements: [
      "Helped establish Madrid Horse Week as one of Europe's leading FEI World Cup events",
      "Successfully delivered eight consecutive editions",
      "Developed long-term relationships with major sponsors and institutional partners",
      "Expanded hospitality, commercial and audience engagement programmes",
      "Strengthened the event's positioning within the international equestrian calendar",
      "Led negotiations with public institutions, venue operators and commercial stakeholders",
      "Contributed to the transformation of the event from a traditional horse show into a broader sports and entertainment experience",
    ],
    scope: [
      "Executive Director",
      "Board Member",
      "Commercial Strategy",
      "Sponsorship",
      "Hospitality",
      "Ticketing",
      "Media Rights",
      "Television Production",
      "Government Relations",
      "Institutional Partnerships",
      "Venue Management",
      "FEI World Cup Event",
    ],
    metrics: [
      { value: "8+", label: "Consecutive editions" },
      { value: "FEI", label: "World Cup status" },
      { value: "IFEMA", label: "Host venue · Madrid" },
      { value: "EU", label: "Institutional relations" },
    ],
    timeline: [], // optional — add edition-by-edition milestones once provided
    lessonsLearned: [], // pending — provide narrative reflections
    gallery: [
      {
        src: "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/b5oixww5_IMG_8994.jpeg",
        caption: "JumpingClash Challenge at Madrid Horse Week — TV Set",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/50umk0f6_mhw4.jpg",
        caption: "Madrid Horse Week Main Arena",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/9jzi4dsq_mhw2.webp",
        caption: "Madison Wicky competing at Madrid Horse Week",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/etud2o7z_mhw3.jpg",
        caption: "FEI World Cup Ceremony",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/t8z0apse_IMG_8993.jpeg",
        caption: "Madrid Horse Week — Executive panel",
      },
    ],
  },

  // ------------------------------------------------------------------
  // NATIONAL EQUESTRIAN LEAGUE — FINAL CONTENT (flagship case study)
  // ------------------------------------------------------------------
  "national-equestrian-league": {
    id: "national-equestrian-league",
    eyebrow: "Experience · Founder & CEO",
    title: "National Equestrian League",
    subtitle: "Founder & CEO",
    headline:
      "Reimagining professional show jumping through teams, media and franchise ownership.",
    years: "2018 — 2020",
    location: "North America",
    hero: {
      image:
        "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/jzfjz6at_794920D9-9597-417F-9CA4-94C490ACCB4B.png",
      imageFit: "contain",
      video: "",
      poster: "",
    },
    summary: [
      `The National Equestrian League was conceived as a transformational project designed to accelerate the professionalisation and commercial growth of show jumping in North America.`,
      `Inspired by the franchise-based structures that have successfully driven growth in major professional sports leagues, the objective was to create a new competitive format capable of attracting investors, sponsors, media partners and new audiences while generating sustainable long-term value for athletes, owners and event organisers.`,
      `As Founder and CEO, I led every aspect of the project from concept creation through regulatory development, commercial strategy, league architecture, team ownership models, media planning and investor engagement.`,
    ],
    mission: `To create the first truly professional team-based show jumping league in North America — designed around franchise ownership, city-based teams, enhanced storytelling, media-friendly competition formats and long-term commercial sustainability. A platform built to increase fan engagement, attract investment and expand the commercial appeal of equestrian sport beyond its traditional audience.`,
    responsibilities: [
      "Founder and creator of the league concept",
      "Development of the complete business model",
      "Design of the sporting and competition structure",
      "Development of FEI-compliant sporting regulations",
      "Direct negotiations with the FEI regarding regulatory approval",
      "Design of franchise ownership and team structures",
      "Creation of sponsorship and commercial rights frameworks",
      "Development of venue integration models",
      "Television and media format development",
      "Investor presentations and fundraising strategy",
      "Intellectual property protection and trademark strategy",
      "Strategic partnerships and stakeholder management",
    ],
    achievements: [
      "Created a complete professional league concept from the ground up",
      "Developed a team-based competition model tailored to the North American market",
      "Designed a franchise structure inspired by major professional sports leagues",
      "Developed the sporting regulations and competitive framework",
      "Established a commercial model integrating sponsorship, media and team ownership",
      "Led discussions with international stakeholders and governing bodies",
      "Created a long-term vision for the commercial evolution of show jumping",
    ],
    scope: [
      "Founder",
      "CEO",
      "League Development",
      "Sports Innovation",
      "Competition Format Design",
      "FEI Regulations",
      "Commercial Strategy",
      "Franchise Development",
      "Media Rights",
      "Sponsorship",
      "Investor Relations",
      "Business Development",
    ],
    metrics: [
      { value: "v1", label: "League architecture" },
      { value: "FEI", label: "Compliant regulations" },
      { value: "NA", label: "Target market" },
      { value: "Franchise", label: "Ownership model" },
    ],
    keyTakeaway: `The National Equestrian League was more than a league concept. It was an attempt to rethink how equestrian sport could be structured, commercialised and presented to future generations of fans, investors and stakeholders. The project reinforced my belief that show jumping possesses significant untapped commercial potential and that innovation will play a critical role in the future growth of the sport.`,
    featuredVideo: {
      eyebrow: "Vision film",
      title: "National Equestrian League",
      url: "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/n7q7hwud_NEL%203.0_1.mp4",
      poster: `${process.env.REACT_APP_BACKEND_URL}/api/static/nel_poster.jpg`,
    },
    timeline: [],
    lessonsLearned: [],
    gallery: [],
  },

  // ------------------------------------------------------------------
  // RYDE TV — FINAL CONTENT
  // Brand is ALWAYS written as "Ryde TV" (two words). Slug stays "ridetv"
  // to preserve external links.
  // ------------------------------------------------------------------
  ridetv: {
    id: "ridetv",
    eyebrow: "Experience · Founder",
    title: "Ryde TV",
    subtitle: "Founder",
    headline:
      "A sports technology and media venture making professional broadcasting accessible to every equestrian event.",
    years: "2022 — 2023",
    location: "Global",
    hero: {
      image:
        "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/xoxxv42z_2CD9B4F1-5629-4CB2-9EDC-1319B4C819CA.png",
      video: "",
      poster: "",
    },
    summary: [
      `Ryde TV was created to solve one of the biggest structural challenges in equestrian sport: the high cost and operational complexity of producing, distributing and monetising professional video content.`,
      `Despite being part of a global industry worth hundreds of billions of euros, most equestrian competitions still lack access to affordable, scalable and professional broadcasting solutions. Traditional production models are expensive, resource-intensive and inaccessible for the majority of organisers.`,
      `As Founder of Ryde TV, I led the creation of a technology-driven media platform designed specifically for equestrian sport. By combining artificial intelligence, automated production, streaming infrastructure and digital content distribution, the goal was to make professional broadcasting accessible to every event, regardless of size or location.`,
    ],
    mission: `To democratise equestrian media by providing organisers, riders and fans with an affordable, scalable and technology-driven platform for content production, distribution and engagement.`,
    responsibilities: [
      "Founded and defined the vision for Ryde TV",
      "Designed the overall business model and commercial strategy",
      "Led product development and platform architecture",
      "Directed the integration of artificial intelligence into automated production workflows",
      "Oversaw relationships with organisers, federations and commercial partners",
      "Defined revenue-sharing opportunities for event organisers",
      "Led fundraising discussions and investor presentations",
      "Coordinated branding, positioning and market strategy",
      "Identified international expansion opportunities",
      "Developed partnerships across the equestrian ecosystem",
      "Supervised technology, media and operational roadmaps",
      "Built the long-term strategy for audience growth and digital engagement",
    ],
    achievements: [
      "Developed one of the first AI-driven automated broadcasting concepts focused exclusively on equestrian sport",
      "Created a platform capable of recording, producing and streaming competitions with minimal human intervention",
      "Designed a scalable solution applicable across multiple disciplines including show jumping, dressage, polo and racing",
      "Introduced a model allowing organisers to reduce production costs while increasing visibility and digital reach",
      "Created new sponsorship and commercial activation opportunities through digital content",
      "Built a platform vision combining media, technology and fan engagement within a single ecosystem",
      "Positioned Ryde TV as a future-oriented sports technology venture rather than a traditional streaming service",
    ],
    scope: [
      "Founder",
      "Sports Technology",
      "Artificial Intelligence",
      "Product Development",
      "Media & Broadcasting",
      "Digital Content",
      "Commercial Strategy",
      "Business Development",
      "Sponsorship",
      "Fan Engagement",
      "International Expansion",
      "Innovation",
    ],
    metrics: [
      { value: "AI", label: "Driven production" },
      { value: "Global", label: "Streaming vision" },
      { value: "Multi", label: "Discipline platform" },
      { value: "Founder", label: "Led venture" },
    ],
    keyTakeaway: `Ryde TV was never conceived as a streaming platform alone. It was designed as a technology and media infrastructure layer for equestrian sport — reducing production costs, expanding audience reach, creating new sponsorship opportunities and helping modernise an industry that remains significantly underserved from a digital perspective.`,
    ecosystem: {
      eyebrow: "The ecosystem",
      headline:
        "A complete equestrian media platform — streaming, event management and automated production, in a single integrated stack.",
      chapters: [
        {
          label: "Fan experience",
          title: "Mobile app & content discovery",
          body: "A native, mobile-first consumer experience built for fans, athletes and owners. Live and on-demand content, athlete and event pages, results and personalised discovery — the front door to every Ryde TV-enabled competition.",
          image:
            "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/9yzuhtbv_87B2D91F-1084-4602-9F77-C0AB35695273.png",
          layout: "vertical",
          alt: "Ryde TV mobile app · content discovery",
        },
        {
          label: "Event organiser",
          title: "Event management dashboard",
          body: "An operator-grade dashboard for organisers — live production, timing and scoring integrations, start lists, results, broadcast control and audience metrics. One tool to run a competition end to end and turn it into media.",
          image:
            "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/10qrhjq1_9FB22D8F-F057-4523-98F3-44E95F2AC625.png",
          layout: "horizontal",
          alt: "Ryde TV event management dashboard",
        },
        {
          label: "Broadcast technology",
          title: "Autonomous camera system",
          body: "A purpose-built autonomous camera and AI production system designed for equestrian venues — automated capture, framing and broadcast with minimal human intervention. The infrastructure that makes professional production affordable for every event.",
          image:
            "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/xoxxv42z_2CD9B4F1-5629-4CB2-9EDC-1319B4C819CA.png",
          layout: "horizontal-hero",
          alt: "Ryde TV autonomous camera system",
        },
      ],
    },
    timeline: [],
    lessonsLearned: [],
    gallery: [],
  },

  // ------------------------------------------------------------------
  // PROFESSIONAL SHOW JUMPER — FINAL CONTENT
  // ------------------------------------------------------------------
  "professional-show-jumper": {
    id: "professional-show-jumper",
    eyebrow: "Experience · Athlete · Horseman · Entrepreneur",
    title: "International Show Jumping Athlete",
    subtitle: "Athlete · Horseman · Entrepreneur",
    headline: "A lifelong connection to the sport from inside the arena.",
    years: "2002 — Present",
    location: "International",
    hero: {
      image:
        "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/r6z3jnt4_94B8AA5C-FC9A-453B-B0B7-886E838C5CE0.jpg",
      video: "",
      poster: "",
    },
    summary: [
      `My career in the equestrian industry began as an athlete.`,
      `For more than two decades, I have been actively involved in show jumping as a rider, horseman and equestrian professional, competing nationally and internationally while developing horses, working with owners, trainers and industry stakeholders across multiple countries.`,
      `This direct involvement in the sport has provided a unique perspective that has shaped every project I have undertaken throughout my professional career.`,
    ],
    mission: `To continuously develop as a rider and horseman while maintaining a practical understanding of the realities, challenges and opportunities facing athletes, owners and equestrian businesses. The experience gained inside the sport has become the foundation for my work in event management, league development, media ventures and commercial projects.`,
    responsibilities: [
      "Competition and performance management",
      "Horse development and training",
      "International horse trading activities",
      "Client and owner relationships",
      "Athlete representation",
      "Competition planning and logistics",
      "Talent identification and horse sourcing",
      "Stable and operational management",
      "International equestrian network development",
      "Industry relationship building",
    ],
    achievements: [
      "More than twenty years actively involved in show jumping",
      "Experience across multiple sectors of the equestrian industry",
      "Development and management of sport horses",
      "International commercial activity within the equestrian sector",
      "Strong network of owners, riders, organisers and industry professionals",
      "First-hand understanding of the needs of athletes and stakeholders",
    ],
    scope: [
      "Show Jumping",
      "Horse Development",
      "Athlete Perspective",
      "International Competition",
      "Horse Trading",
      "Client Relations",
      "Industry Network",
      "Operations",
      "Business Development",
    ],
    metrics: [
      { value: "20+", label: "Years in the sport" },
      { value: "Intl.", label: "Competition level" },
      { value: "Horses", label: "Developed & traded" },
      { value: "Network", label: "Owners · riders · organisers" },
    ],
    keyTakeaway: `Being actively involved in the sport has provided an understanding of the equestrian industry that cannot be learned from outside the arena. It has allowed me to approach every business, media and sporting project from the perspective of the people who ultimately make the sport possible: riders, owners, horses and organisers.`,
    timeline: [],
    lessonsLearned: [],
    gallery: [
      {
        src: "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/l7qv66m2_IMG_5687.JPG",
        caption: "International show jumping · Bay over flamingo",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/19a924dl_IMG_5753%202.JPG",
        caption: "International show jumping · White over peacock",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/clbrzw17_IMG_5881%203.jpg",
        caption: "International show jumping · Pablo Marquez",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/12qxulof_IMG_5882%202.JPG",
        caption: "International show jumping · Pablo Marquez",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/r6z3jnt4_94B8AA5C-FC9A-453B-B0B7-886E838C5CE0.jpg",
        caption: "International show jumping · Pablo Marquez",
      },
    ],
  },
};

export const caseStudyOrder = [
  "national-equestrian-league",
  "madrid-horse-week",
  "ridetv",
  "professional-show-jumper",
];

export default caseStudies;
